import { useEffect, useRef } from 'react';
import { trackSearchLoadEvent, EVENTS } from '../../../../../redux-stores/KeywordSearch/thunk';
import KeywordInputSearch from './KeywordInputSearch';
import KeywordSearchResultsDetails from './KeywordSearchResultsDetails';
import KeywordSearchFirstLevel from './KeywordSearchFirstLevel';
import KeywordSearchEntityList from './KeywordSearchEntityList';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';
import { ReactComponent as ArrowBackIcon } from 'common/images/arrow_back.svg';
import { DotsIndicator } from 'common/components';
import { Conversation } from 'types';

const classes = BEM.with('KeywordSearchSideBar');

const {
  setKeywordSearchMode,
  setKeywordSearchTerm,
  setPreviousConversationId,
  setSelectedConversationId,
  setSelectedHighlightedMessageId,
  setSelectedMessageId,
} = actions;

type MobxProps = {
  setCurrentConversationId: (id: string) => void;
  setKeywordSearchMsgSortNumber: (sortNumber: number) => void;
  setKeywordSearchScrollTarget: (ref: HTMLElement | null) => void;
  isKeywordInboxSearchPhase2FeatureFlagEnabled: boolean;
  currentConversationId: string;
  currentConversation: Conversation | null;
};

const KeywordSearchSideBar = ({
  currentConversationId,
  currentConversation,
  isKeywordInboxSearchPhase2FeatureFlagEnabled,
  setCurrentConversationId,
  setKeywordSearchMsgSortNumber,
  setKeywordSearchScrollTarget,
}: MobxProps) => {
  const dispatch = useAppDispatch();
  const {
    previousConversationId,
    selectedConversationId,
    isFetching,
    keywordSearchTerm,
    loadMessageStartTime,
  } = useAppSelector((state) => state.keywordSearch);

  const prevConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentConversationId && loadMessageStartTime) {
      if (!currentConversation) {
        dispatch(trackSearchLoadEvent(EVENTS.OPEN_CONV_FOR_KWS_MESSAGE_FAIL));
        return;
      }

      const kwsLoadEventName =
        currentConversationId === prevConversationIdRef.current
          ? EVENTS.CONV_LOADED_ALREADY_OPEN
          : EVENTS.CONV_LOADED_FRESH;
      dispatch(trackSearchLoadEvent(kwsLoadEventName));
      prevConversationIdRef.current = currentConversationId;
    }
  }, [currentConversationId, currentConversation, dispatch, loadMessageStartTime]);

  useEffect(
    function cleanUp() {
      return () => {
        dispatch(setKeywordSearchMode(false));
        setCurrentConversationId(previousConversationId);
        dispatch(trackSearchLoadEvent(EVENTS.EXIT_KEYWORD_SEARCH));
        dispatch(setPreviousConversationId(''));
        dispatch(setSelectedMessageId(''));
        dispatch(setSelectedHighlightedMessageId(''));
        dispatch(setSelectedConversationId(''));
        dispatch(setKeywordSearchTerm(''));
        setKeywordSearchMsgSortNumber(0);
        setKeywordSearchScrollTarget(null);
      };
    },
    [
      dispatch,
      setKeywordSearchMsgSortNumber,
      setKeywordSearchScrollTarget,
      setCurrentConversationId,
      previousConversationId,
    ]
  );

  return (
    <div className={classes()}>
      <div className={classes('search-container')}>
        {selectedConversationId && (
          <ArrowBackIcon
            className={classes('arrow-back')}
            onClick={() => {
              dispatch(setSelectedConversationId(''));
            }}
          />
        )}
        <KeywordInputSearch isAConversationSelected={selectedConversationId !== ''} />
        {selectedConversationId === '' && (
          <p
            className={classes('label')}
            onClick={() => {
              dispatch(setKeywordSearchMode(false));
              setCurrentConversationId(previousConversationId);
            }}
          >
            CANCEL
          </p>
        )}
      </div>
      {isFetching && keywordSearchTerm && (
        <div className={classes('results-loader')}>
          <DotsIndicator className={classes('dots')} size={13} speed={0.5} />
        </div>
      )}
      <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
        {isKeywordInboxSearchPhase2FeatureFlagEnabled ? (
          <KeywordSearchEntityList />
        ) : (
          <KeywordSearchFirstLevel />
        )}
        {selectedConversationId && <KeywordSearchResultsDetails />}
      </div>
    </div>
  );
};

export default mobxInjectSelect<null, MobxProps>({
  conversationStore: [
    'setCurrentConversationId',
    'setKeywordSearchMsgSortNumber',
    'setKeywordSearchScrollTarget',
    'currentConversationId',
    'currentConversation',
  ],
  messengerStore: ['isKeywordInboxSearchPhase2FeatureFlagEnabled'],
})(KeywordSearchSideBar);
